import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Save, Ruler, Weight, Calendar, Target, Dumbbell, Share2, Watch, Building2, BookOpen, ChevronRight, Activity, Bike, Timer, Heart, Flame, Droplet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SPORTS = [
  { id: "running", name: "Futás", icon: Activity },
  { id: "cycling", name: "Kerékpár", icon: Bike },
  { id: "strength", name: "Erőedzés", icon: Dumbbell },
  { id: "bodyweight", name: "Saját testsúlyos", icon: Flame },
  { id: "swimming", name: "Úszás", icon: Droplet },
  { id: "triathlon", name: "Triatlon", icon: Timer },
  { id: "fitness", name: "Fitness", icon: Heart },
];

export default function ProfilePage() {
  const { api, user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    api().get("/profile").then(r => {
      const data = r.data;
      const sports = data.sports?.length ? data.sports : (data.sport ? [data.sport] : []);
      setProfile({ ...data, sports });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api().put("/profile", {
        name: profile.name,
        height_cm: profile.height_cm,
        weight_kg: profile.weight_kg,
        age: profile.age,
        sports: profile.sports || [],
        goal: profile.goal
      });
      setProfile(res.data);
      setUser({ ...user, name: res.data.name, sports: res.data.sports, sport: res.data.sport });
      toast.success("Profil mentve!");
    } catch {
      toast.error("Hiba a profil mentésekor");
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) => setProfile(prev => ({ ...prev, [key]: value }));

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api().get("/report/weekly-pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `athlevity_heti_riport_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Heti riport letöltve!");
    } catch {
      toast.error("Hiba a riport letöltésekor.");
    } finally {
      setPdfLoading(false);
    }
  };

  const toggleSport = (id) => {
    setProfile(prev => {
      const current = prev?.sports || [];
      const next = current.includes(id) ? current.filter(s => s !== id) : [...current, id];
      return { ...prev, sports: next, sport: next[0] || "" };
    });
  };

  if (loading || !profile) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:pt-20" data-testid="profile-page">
      <div className="flex items-center justify-between fade-in-up">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Profil</h1>
        <User className="text-[#00f260]" size={24} />
      </div>

      {/* Avatar & Name */}
      <GlassCard glow testId="profile-avatar-card" className="text-center py-8 fade-in-up">
        <div className="w-20 h-20 rounded-full bg-[#00f260]/20 flex items-center justify-center mx-auto mb-4">
          <User size={36} className="text-[#00f260]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-white">{profile.name}</h2>
        <p className="text-sm text-gray-400 mt-1">{profile.email}</p>
      </GlassCard>

      {/* Profile Form */}
      <GlassCard testId="profile-form-card" className="fade-in-up fade-in-up-1">
        <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Személyes adatok</p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs flex items-center gap-2"><User size={12} /> Név</Label>
            <Input
              data-testid="profile-name-input"
              value={profile.name || ""}
              onChange={(e) => update("name", e.target.value)}
              className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs flex items-center gap-2"><Ruler size={12} /> Magasság (cm)</Label>
              <Input
                data-testid="profile-height-input"
                type="number"
                value={profile.height_cm || ""}
                onChange={(e) => update("height_cm", parseFloat(e.target.value))}
                className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs flex items-center gap-2"><Weight size={12} /> Testsúly (kg)</Label>
              <Input
                data-testid="profile-weight-input"
                type="number"
                value={profile.weight_kg || ""}
                onChange={(e) => update("weight_kg", parseFloat(e.target.value))}
                className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs flex items-center gap-2"><Calendar size={12} /> Kor</Label>
              <Input
                data-testid="profile-age-input"
                type="number"
                value={profile.age || ""}
                onChange={(e) => update("age", parseInt(e.target.value))}
                className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs flex items-center gap-2"><Dumbbell size={12} /> Fő sport</Label>
              <div
                data-testid="profile-primary-sport"
                className="bg-black/20 border border-white/10 text-white rounded-xl h-11 flex items-center px-3 text-sm"
              >
                {SPORTS.find((s) => s.id === profile.sports?.[0])?.name || "Nincs kiválasztva"}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs flex items-center gap-2"><Target size={12} /> Sportágak (több is választható)</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="profile-sports-grid">
              {SPORTS.map((s) => {
                const Icon = s.icon;
                const selected = profile.sports?.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`profile-sport-${s.id}`}
                    onClick={() => toggleSport(s.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs transition-all ${
                      selected
                        ? "bg-[#00f260]/10 border-[#00f260]/50 text-[#00f260]"
                        : "bg-white/5 border-white/10 text-gray-300 hover:border-white/20"
                    }`}
                  >
                    <Icon size={14} className={selected ? "text-[#00f260]" : "text-gray-400"} />
                    <span>{s.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs flex items-center gap-2"><Target size={12} /> Cél</Label>
            <Input
              data-testid="profile-goal-input"
              value={profile.goal || ""}
              onChange={(e) => update("goal", e.target.value)}
              className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
            />
          </div>
        </div>
      </GlassCard>

      {/* Share & PDF Report */}
      <GlassCard testId="profile-share-card" glow className="fade-in-up fade-in-up-2">
        <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate("/share")}>
          <div className="w-12 h-12 rounded-full bg-[#00f260]/10 flex items-center justify-center">
            <Share2 size={22} className="text-[#00f260]" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-white font-medium">Teljesítmény megosztás</p>
            <p className="text-xs text-gray-400">Jelvények, statisztikák, közösségi megosztás</p>
          </div>
          <ChevronRight size={18} className="text-gray-500" />
        </div>
      </GlassCard>

      <GlassCard testId="profile-pdf-card" className="fade-in-up fade-in-up-2">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
            <FileText size={22} className="text-red-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-white font-medium">Heti PDF riport</p>
            <p className="text-xs text-gray-400">Edzés, táplálkozás és teljesítmény összefoglaló</p>
          </div>
          <button
            data-testid="download-pdf-btn"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            className="neon-btn text-xs px-4 py-2 flex items-center gap-2"
          >
            {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            {pdfLoading ? "Generálás..." : "Letöltés"}
          </button>
        </div>
      </GlassCard>

      {/* Quick Links */}
      <div className="space-y-3 fade-in-up fade-in-up-3">
        <ProfileLink testId="profile-wearables-link" icon={Watch} label="Wearable Integrációk" desc="Apple Health, Garmin, Strava, Whoop" color="#64b5f6" onClick={() => navigate("/wearables")} />
        <ProfileLink testId="profile-academy-link" icon={BookOpen} label="Academy" desc="Tudásbázis és kurzusok" color="#ffc107" onClick={() => navigate("/academy")} />
        <ProfileLink testId="profile-corporate-link" icon={Building2} label="Corporate Wellness" desc="Vállalati megoldások" color="#e040fb" onClick={() => navigate("/corporate")} />
      </div>

      {/* Save */}
      <button
        data-testid="profile-save-btn"
        onClick={handleSave}
        disabled={saving}
        className="neon-btn w-full text-sm flex items-center justify-center gap-2"
      >
        <Save size={16} />
        {saving ? "Mentés..." : "Profil mentése"}
      </button>

      {/* Logout */}
      <button
        data-testid="logout-btn"
        onClick={logout}
        className="w-full py-3 px-6 rounded-full border border-white/10 text-gray-400 hover:text-red-400 hover:border-red-400/30 transition-all text-sm flex items-center justify-center gap-2"
      >
        <LogOut size={16} />
        Kijelentkezés
      </button>
    </div>
  );
}

function ProfileLink({ testId, icon: Icon, label, desc, color, onClick }) {
  return (
    <GlassCard testId={testId} className="cursor-pointer" onClick={onClick}>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={20} style={{ color }} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-white font-medium">{label}</p>
          <p className="text-xs text-gray-400">{desc}</p>
        </div>
        <ChevronRight size={18} className="text-gray-500" />
      </div>
    </GlassCard>
  );
}
